<template>
  <div />
</template>

<!-- eslint-disable no-underscore-dangle -->
<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'FreeGift',

  data() {
    return {
      freeGiftProduct: false,
      state: 'ready',
      conditionProductExists: false,
      conditionCollectionExists: false,
      conditionThresholdReached: false,
      isGiftInCart: false,
      freeGiftWasCanceled: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartAttributes: (state) => state.cart.response.attributes,
    }),

  },

  watch: {

    /**
     * Watch cart items to update focus trap.
     */
    'cart.items': {
      handler() {
        this.checkGiftEligible()
      },
      immediate: true,
    },

    /**
     * Watch cart attributes to update focus trap.
     */
    'cart.attributes'() {
      this.checkGiftEligible()
    },

  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      removeItem: 'cart/updateItem',
      updateCartAttributes: 'cart/updateCartAttributes',
    }),

    /**
     * Assess if customer is eligible for free gift and adds/removes the gift
     */
    checkGiftEligible() {
      this.setData()

      if (this.shouldAddFreeGift()) {
        this.addFreeGiftToCart()
      } else if (this.shouldRemoveFreeGift()) {
        if (this.isGiftInCart) {
          this.removeGiftItems()
        }
      }
    },

    /**
     * Assigns variables on data change
     */
    setData() {
      this.freeGiftProduct = this.$variable('settings.fgwp.product')
      this.conditionProductExists = this.checkProductOfCondition()
      this.conditionCollectionExists = this.checkCollectionOfCondition()
      this.conditionThresholdReached = this.checkThresholdCondition()
      this.isGiftInCart = this.checkFreeGiftInCart()
      this.freeGiftWasCanceled = this.checkFreeGiftWasCanceled()
    },

    /**
     * Check if free gift conditions are met
     */
    shouldAddFreeGift() {
      if (localStorage.getItem('fgwp_canceled') === 'true') {
        return false
      }

      return this.freeGiftProduct &&
        !this.isGiftInCart &&
        this.state === 'ready' &&
        !this.freeGiftWasCanceled &&
        (
          this.conditionProductExists ||
          this.conditionCollectionExists ||
          this.conditionThresholdReached
        )
    },

    /**
     * Check if free gift can be removed
     */
    shouldRemoveFreeGift() {
      return this.state === 'ready' &&
        !this.conditionProductExists &&
        !this.conditionCollectionExists &&
        !this.conditionThresholdReached
    },

    /**
     * Check if gift is in cart
     */
    checkFreeGiftInCart() {
      return this.cart.items?.some((item) => {
        return item.properties?._is_fgwp
      })
    },

    /**
     * Check if user removed gift from cart
     */
    checkFreeGiftWasCanceled() {
      return this.cart.attributes.fgwp_canceled === true || this.cart.attributes.fgwp_canceled === 'true'
    },

    /**
     * Check if condition product exists
     */
    checkProductOfCondition() {
      const conditionProduct = this.$variable('settings.fgwp.conditionProduct')
      const conditionProductVariant = this.$variable('settings.fgwp.conditionProductVariant')

      return this.cart.items?.some((item) => item.product_id === conditionProduct) ||
        this.cart.items?.some((item) => item.variant_id === conditionProductVariant)
    },

    /**
     * Check if condition collection exists
     */
    checkCollectionOfCondition() {
      const conditionCollection = this.$variable('settings.fgwp.conditionCollection')

      return this.cart.items?.some((item) => {
        return item.collections?.some(
          (collection) => collection.id === conditionCollection && !item.properties?._is_fgwp,
        )
      })
    },

    /**
     * Check if condition threshold exists
     */
    checkThresholdCondition() {
      const conditionThreshold = this.$variable('settings.fgwp.conditionThreshold')

      return conditionThreshold > 0 && this.cart?.items_subtotal_price / 100 >= conditionThreshold
    },

    /**
     * Add free gift to cart
     */
    async addFreeGiftToCart() {
      this.state = 'adding'
      const item = [
        {
          id: this.freeGiftProduct,
          quantity: 1,
          properties: {
            _is_fgwp: true,
          },
        },
      ]

      try {
        await this.addItem(item)
        this.state = 'ready'
      } catch (err) {
        this.state = 'error'
      }
    },

    /**
      * Remove gift items
      */
    async removeGiftItems() {
      const gifts = this.cart?.items?.filter(item => item.properties?._is_fgwp)
      const removeQueries = gifts.map((item) => {
        return {
          id: item.key,
          quantity: 0,
        }
      })

      await this.removeItem(removeQueries)
    },

    /**
     * Check if user had removed free gift from cart
     */
    isFreeGiftCanceled() {
      return this.cartAttributes?.fgwp_canceled === true || this.cartAttributes?.fgwp_canceled === 'true'
    },

    /**
     * Handle quantity update event.
     */
    async resetCartAttribute() {
      try {
        await this.updateCartAttributes({
          attributes: {
            ...this.cartAttributes,
            fgwp_canceled: false,
          },
        })
      } catch (error) {
        // do nothing
      }
    },

  },

}
</script>
